// BeginCheckout.jsx
import React, { useEffect, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { Elements } from "@stripe/react-stripe-js"
import AldooStoreAPI from "aldoo-ra/Store/api"
import { api } from "aldoo-ra/API"
import Value from "aldoo-ra/Value"
import { getStripePromise } from "aldoo-ra/Store/stripe-client"
import { WalletIcon } from "@heroicons/react/24/outline"
import PurchaseFlowController from "aldoo-ra/Store/PurchaseFlowController"

const stripePromise = getStripePromise()
let paymentLoading = false

const themeColor = (varName) => {
  if (typeof window === "undefined") return undefined
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  return value ? `rgb(${value})` : undefined
}

function CheckoutContent({ store, controller }) {
  const texts = TextBundle({ id: "aldoo-store" })
  const [paymentInfo] = Value("aldoo-payment-info")
  const [preloadedInvoiceData, setPreloadedInvoiceData] = Value("aldoo-preloaded-invoice-data")
  const [showingNewPayment, setShowingNewPayment] = useState(false)
  const [showingInvoice, setShowingInvoice] = useState(false)
  const [invoiceData, setInvoiceData] = useState(() => preloadedInvoiceData || null)
  const [invoiceStepWasShown, setInvoiceStepWasShown] = useState(false)

  const currentStep = controller.currentStep
  const { canContinue, loading, continueText, hideButtons, handleContinue, handleBack, getFlowSteps, isStepActive } = controller

  const {
    cart,
    setPaymentInfo,
    checkoutVisible,
    emailDialogVisible,
    hideCheckout,
    getElement,
    getFragment,
    cartChanged,
    purchaseFlow,
  } = store

  const CheckoutForm = getElement("checkout-form")
  const ReusePaymentDialog = getElement("reuse-payment-dialog")
  const InvoiceStep = getElement("invoice-step")
  const EmailInputDialog = getElement("email-input-dialog")
  const CheckoutWithInvoice = getElement("checkout-with-invoice")
  const CheckoutPreparingPayment = getFragment("checkout-preparing-payment")
  const CheckoutContainer = getFragment("checkout-container")

  useEffect(() => {
    if (checkoutVisible && preloadedInvoiceData && !invoiceData) {
      setInvoiceData({
        ...preloadedInvoiceData,
        useSavedBillingDetails: true
      })
      setPreloadedInvoiceData(null)
    }
  }, [checkoutVisible, preloadedInvoiceData])

  useEffect(() => {
    if (currentStep === 'payment-reuse' && showingNewPayment) {
      setShowingNewPayment(false)
    }
  }, [currentStep])

  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }, [currentStep])

  useEffect(() => {
    const validateAndRefreshPaymentInfo = async () => {
      if (!cart || cart.length === 0) return
      if (paymentLoading) return

      const cartItem = cart[0]
      const pidMatches = !cartItem?.pid || !paymentInfo?.pid || paymentInfo.pid === cartItem.pid

      if (paymentInfo && !cartChanged && pidMatches && paymentInfo.savedPaymentMethod) {
        const savedMethod = paymentInfo.savedPaymentMethod
        const currentYear = new Date().getFullYear()
        const currentMonth = new Date().getMonth() + 1

        const isExpiredOrExpiring = savedMethod.type === 'card' &&
          (savedMethod.expiryYear < currentYear ||
           (savedMethod.expiryYear === currentYear && savedMethod.expiryMonth <= currentMonth))

        if (!isExpiredOrExpiring) {
          try {
            const currentMethods = await AldooStoreAPI.getPaymentMethods()
            const currentDefaultMethod = currentMethods.find(method => method.isDefault)

            if (!currentDefaultMethod || currentDefaultMethod.id !== savedMethod.id ||
                currentDefaultMethod.expiryYear !== savedMethod.expiryYear ||
                currentDefaultMethod.expiryMonth !== savedMethod.expiryMonth) {
              // Force refresh - fall through
            } else {
              setPaymentInfo({ ...paymentInfo })
              return
            }
          } catch (error) {
            console.error("Failed to validate payment methods:", error)
          }
        }
      } else if (paymentInfo && !cartChanged && pidMatches) {
        setPaymentInfo({ ...paymentInfo })
        return
      }

      paymentLoading = true

      try {
        let response

        if (store.customAuth) {
          const storeAPIWithAuth = api("store", store.customAuth)
          response = await storeAPIWithAuth({
            initiate_payment: {
              platform: "Web",
              pid: cartItem.pid,
              promo_code: store.promoCode,
            }
          })
        } else {
          response = await AldooStoreAPI.initializePayment({
            pid: cartItem.pid,
            promo_code: store.promoCode,
          })
        }

        paymentLoading = false

        if (response.error) {
          console.error("Failed to initialize payment:", response.error)
          return
        }

        response.pid = cartItem.pid
        response.promoCode = store.promoCode
        setPaymentInfo(response)
      } catch (error) {
        console.error("Failed to initialize payment:", error)
        paymentLoading = false
      }
    }

    if (checkoutVisible) {
      const hasAuth = store.auth || store.tempAuthForPurchase
      if (!paymentInfo && hasAuth) {
        validateAndRefreshPaymentInfo()
      }
      setInvoiceStepWasShown(false)
    } else {
      setInvoiceData(null)
      setShowingInvoice(false)
      setShowingNewPayment(false)
    }
  }, [checkoutVisible, invoiceData, preloadedInvoiceData, store.auth, store.tempAuthForPurchase])

  useEffect(() => {
    const handlePaymentMethodsUpdated = () => {
      if (!checkoutVisible || !cart || cart.length === 0) return
      const cartItem = cart[0]

      const initPayment = store.customAuth
        ? api("store", store.customAuth)({
            initiate_payment: {
              platform: "Web",
              pid: cartItem.pid,
              promo_code: store.promoCode,
            }
          })
        : AldooStoreAPI.initializePayment({
            pid: cartItem.pid,
            promo_code: store.promoCode,
          })

      initPayment.then(response => {
        if (!response.error) {
          response.pid = cartItem.pid
          response.promoCode = store.promoCode
          setPaymentInfo(response)
        }
      }).catch(error => {
        console.error("Failed to refresh payment info:", error)
      })
    }

    window.addEventListener("payment-methods-updated", handlePaymentMethodsUpdated)
    return () => {
      window.removeEventListener("payment-methods-updated", handlePaymentMethodsUpdated)
    }
  }, [checkoutVisible, cart, store.promoCode])

  if (paymentInfo && !paymentInfo.requiresPayment) return null
  if (!checkoutVisible) return null

  const shouldShowReuseDialog =
    currentStep === 'payment-reuse' &&
    paymentInfo?.savedPaymentMethod &&
    !showingNewPayment &&
    !showingInvoice

  if (invoiceData && !store.invoiceData) {
    store.invoiceData = invoiceData
  }

  const flowSteps = getFlowSteps()

  const getCurrentStepIndex = () => {
    if (!purchaseFlow) return -1
    const displayStep = currentStep === 'payment-reuse' ? 'payment' : currentStep
    return flowSteps.findIndex(s => s.id === displayStep)
  }

  const renderFlowProgress = () => {
    const steps = flowSteps
    const currentIndex = getCurrentStepIndex()

    if (steps.length === 0) return null

    return (
      <div className="flex items-center mb-4 py-3 overflow-x-auto scrollbar-none">
        {steps.map((step, index) => {
          const isActive = index === currentIndex
          const isPast = index < currentIndex
          const isLast = index === steps.length - 1

          return (
            <React.Fragment key={step.id}>
              <div className="flex-1 flex items-center justify-center gap-1.5 min-w-[80px]">
                {isPast && (
                  <svg className="w-3.5 h-3.5 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
                <div
                  className={`
                    text-xs sm:text-sm font-medium text-center whitespace-nowrap transition-all pb-1
                    ${isActive ? 'text-text border-b-4 border-primary font-semibold' : ''}
                    ${isPast ? 'text-primary' : ''}
                    ${!isActive && !isPast ? 'text-text_secondary' : ''}
                  `}
                >
                  {step.name}
                </div>
              </div>
              {!isLast && (
                <div className="w-px h-5 bg-bordered flex-shrink-0"></div>
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  const renderFooter = () => {
    if (hideButtons) return null

    const isPaymentStep = currentStep === 'payment' || currentStep === 'payment-reuse'
    const buttonText = continueText

    const isPaymentAction = buttonText && (
      buttonText.toLowerCase().includes('pay') ||
      buttonText.toLowerCase().includes('submit')
    )
    const showWalletIcon = isPaymentStep && canContinue && !loading && isPaymentAction

    return (
      <div className="flex gap-3 mt-4">
        <Button
          variant="secondary"
          onClick={(e) => {
            e.currentTarget.blur()
            handleBack()
          }}
          disabled={loading}
          className="flex-1 !h-auto p-4"
        >
          {texts?.back || "Back"}
        </Button>

        <Button
          variant="primary"
          onClick={(e) => {
            e.currentTarget.blur()
            handleContinue()
          }}
          disabled={!canContinue || loading}
          className={`flex-1 !h-auto p-4 gap-2 relative ${!canContinue || loading ? 'opacity-50' : ''}`}
        >
          {loading && (
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {showWalletIcon && (
            <WalletIcon className="h-5 w-5 text-white" />
          )}
          {loading ? (currentStep === 'email' ? (texts?.verifying || "Verifying...") : (texts?.loading || "Loading...")) : buttonText}
        </Button>
      </div>
    )
  }

  const stripeOptions = (clientSecret) => clientSecret ? {
    clientSecret,
    loader: "always",
    appearance: {
      variables: {
        colorDanger: themeColor("--color-error"),
        colorBackground: themeColor("--color-paper"),
        colorText: themeColor("--color-text"),
      },
    },
  } : null

  const renderAllSteps = () => (
    <>
      {/* Email step */}
      <div className={currentStep === 'email' ? '' : 'hidden'}>
        <EmailInputDialog
          store={store}
          initialEmail={store.auth?.email || store.tempAuthForPurchase?.email || store.purchaseUserEmail || ""}
          isActive={isStepActive('email')}
        />
      </div>

      {/* Invoice step */}
      <div className={currentStep === 'invoice' ? 'h-full' : 'hidden'}>
        {paymentInfo && paymentInfo.clientSecret ? (
          <Elements stripe={stripePromise} options={stripeOptions(paymentInfo.clientSecret)}>
            <CheckoutWithInvoice store={store} invoiceData={invoiceData}>
              <InvoiceStep store={store} isOpen={currentStep === 'invoice'} />
            </CheckoutWithInvoice>
          </Elements>
        ) : (
          <CheckoutPreparingPayment />
        )}
      </div>

      {/* Payment step */}
      <div className={(currentStep === 'payment' || currentStep === 'payment-reuse') ? 'h-full' : 'hidden'}>
        {paymentInfo && paymentInfo.clientSecret ? (
          <Elements stripe={stripePromise} options={stripeOptions(paymentInfo.clientSecret)}>
            <CheckoutWithInvoice store={store} invoiceData={invoiceData}>
              {shouldShowReuseDialog ? (
                <div className="h-full flex flex-col justify-between">
                  <ReusePaymentDialog
                    store={store}
                    onSelectNewPayment={() => setShowingNewPayment(true)}
                    isActive={isStepActive('payment-reuse')}
                  />
                </div>
              ) : (
                <CheckoutForm store={store} isActive={isStepActive('payment')} />
              )}
            </CheckoutWithInvoice>
          </Elements>
        ) : (
          <CheckoutPreparingPayment />
        )}
      </div>
    </>
  )

  const isLoadingInitialData = checkoutVisible && currentStep !== 'email' && !paymentInfo

  return (
    <Modal
      isOpen={checkoutVisible || emailDialogVisible}
      variant="store_checkout"
      className="md:w-[750px] md:!max-h-[85vh]"
      onClose={hideCheckout}
      allowCloseBtn={false}
    >
      <div className="flex flex-col h-full md:h-auto md:max-h-[calc(85vh-2rem)]">
        <div className="flex-shrink-0">
          {renderFlowProgress()}
        </div>
        <div className="flex-1 bg-paper rounded-2xl border border-gray-100 min-h-0 relative overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper">
          {isLoadingInitialData ? (
            <CheckoutPreparingPayment />
          ) : (currentStep === 'email' || currentStep === 'invoice') ? (
            <div className="min-h-full flex items-center justify-center py-4">
              <CheckoutContainer store={store}>
                {renderAllSteps()}
              </CheckoutContainer>
            </div>
          ) : (
            <div className="h-full">
              <CheckoutContainer store={store}>
                {renderAllSteps()}
              </CheckoutContainer>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 pb-safe">
          {renderFooter()}
        </div>
      </div>
    </Modal>
  )
}

export default function BeginCheckout({ store }) {
  return (
    <PurchaseFlowController store={store}>
      {(controller) => <CheckoutContent store={store} controller={controller} />}
    </PurchaseFlowController>
  )
}
